import { useEffect, useRef, useState } from "react";
import { getObsConfig } from "@/lib/api";
import { ICE_CONFIG } from "@/lib/iceConfig";
import { logWarn } from "@/lib/log";

const PING_INTERVAL_MS = 20000;
const RECONNECT_DELAY_MS = 3000;
const CONFIG_RETRY_MS = 5000;

const wsSend = (ctx, data) => {
    if (ctx.wsRef.current?.readyState === 1) ctx.wsRef.current.send(JSON.stringify(data));
};

function closeViewerPeer(ctx) {
    try {
        ctx.pcRef.current?.close();
    } catch (e) {
        logWarn("[SPCLIVE MIRROR] pc close failed", e);
    }
    ctx.pcRef.current = null;
    ctx.offerIdRef.current = null;
    ctx.pendingIceRef.current = [];
    if (ctx.remoteVideoRef.current) ctx.remoteVideoRef.current.srcObject = null;
}

function attachPeerHandlers(ctx, pc) {
    // Accumulate tracks into one stream: ontrack fires once per m-line (video then audio),
    // and assigning a fresh per-event stream would drop the previously received kind.
    const acc = new MediaStream();
    pc.ontrack = (e) => {
        if (ctx.isDisposed() || ctx.pcRef.current !== pc) return;
        acc.getTracks()
            .filter((t) => t.kind === e.track.kind)
            .forEach((t) => acc.removeTrack(t));
        acc.addTrack(e.track);
        const el = ctx.remoteVideoRef.current;
        if (el) {
            if (el.srcObject !== acc) el.srcObject = acc;
            el.play?.().catch(() => {});
        }
    };
    pc.onconnectionstatechange = () => {
        if (ctx.isDisposed() || ctx.pcRef.current !== pc) return;
        if (pc.connectionState === "connected") {
            ctx.setStatus("live");
        } else if (["failed", "disconnected", "closed"].includes(pc.connectionState)) {
            ctx.setStatus("waiting");
        }
    };
    pc.onicecandidate = (e) => {
        if (e.candidate) {
            wsSend(ctx, { type: "ice", candidate: e.candidate, offer_id: ctx.offerIdRef.current });
        }
    };
}

async function handleOffer(ctx, msg) {
    closeViewerPeer(ctx);
    const pc = new RTCPeerConnection(ICE_CONFIG);
    ctx.pcRef.current = pc;
    ctx.offerIdRef.current = msg.offer_id || null;
    attachPeerHandlers(ctx, pc);
    await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
    ctx.pendingIceRef.current.forEach((c) => pc.addIceCandidate(c).catch(() => {}));
    ctx.pendingIceRef.current = [];
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    wsSend(ctx, { type: "answer", sdp: answer.sdp, offer_id: ctx.offerIdRef.current });
}

async function applyPublisherIce(ctx, msg) {
    if (msg.offer_id && ctx.offerIdRef.current && msg.offer_id !== ctx.offerIdRef.current) return;
    if (ctx.pcRef.current?.remoteDescription) {
        await ctx.pcRef.current.addIceCandidate(msg.candidate).catch(() => {});
    } else {
        ctx.pendingIceRef.current.push(msg.candidate);
    }
}

async function handleViewerMessage(ctx, msg) {
    if (msg.type === "offer") {
        await handleOffer(ctx, msg);
    } else if (msg.type === "ice" && msg.candidate) {
        await applyPublisherIce(ctx, msg);
    } else if (msg.type === "publisher-left" || msg.type === "no-publisher") {
        closeViewerPeer(ctx);
        if (!ctx.isDisposed()) ctx.setStatus("waiting");
    }
}

function openViewerSocket(ctx, obsToken, control) {
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/ws/mirror/${obsToken}?role=viewer`);
    ctx.wsRef.current = ws;
    let pingTimer = null;

    ws.onopen = () => {
        if (ctx.isDisposed() || ctx.wsRef.current !== ws) return;
        ctx.setStatus("waiting");
        pingTimer = setInterval(() => {
            if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
        }, PING_INTERVAL_MS);
    };
    ws.onmessage = async (ev) => {
        if (ctx.isDisposed() || ctx.wsRef.current !== ws) return;
        try {
            await handleViewerMessage(ctx, JSON.parse(ev.data));
        } catch (e) {
            logWarn("[SPCLIVE MIRROR] viewer signaling error", e);
        }
    };
    ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        if (ctx.isDisposed() || ctx.wsRef.current !== ws) return;
        closeViewerPeer(ctx);
        ctx.setStatus("connecting");
        control.scheduleReconnect(RECONNECT_DELAY_MS);
    };
}

async function connectViewer(ctx, obsToken, control) {
    if (ctx.isDisposed()) return;
    try {
        await getObsConfig(obsToken);
    } catch (e) {
        if (e.response?.status === 404) {
            if (!ctx.isDisposed()) ctx.setStatus("invalid");
            return;
        }
        // transient network error — retry
        control.scheduleReconnect(CONFIG_RETRY_MS);
        return;
    }
    if (ctx.isDisposed()) return;
    openViewerSocket(ctx, obsToken, control);
}

export function useMirrorViewer({ obsToken }) {
    const remoteVideoRef = useRef(null);
    const wsRef = useRef(null);
    const pcRef = useRef(null);
    const offerIdRef = useRef(null);
    const pendingIceRef = useRef([]);

    // connecting | waiting | live | invalid
    const [status, setStatus] = useState("connecting");

    useEffect(() => {
        if (!obsToken) return undefined;
        let disposed = false;
        let reconnectTimer = null;

        const ctx = {
            wsRef,
            pcRef,
            offerIdRef,
            pendingIceRef,
            remoteVideoRef,
            setStatus,
            isDisposed: () => disposed,
        };
        const control = {
            scheduleReconnect: (delayMs) => {
                reconnectTimer = setTimeout(() => connectViewer(ctx, obsToken, control), delayMs);
            },
        };
        connectViewer(ctx, obsToken, control);

        return () => {
            disposed = true;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            closeViewerPeer(ctx);
            try {
                wsRef.current?.close();
            } catch (e) {
                logWarn("[SPCLIVE MIRROR] ws close failed", e);
            }
            wsRef.current = null;
        };
    }, [obsToken]);

    return { remoteVideoRef, status };
}
